import React, { useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Building, Users, Mail, Phone, MapPin, Briefcase, Calendar } from 'lucide-react';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { GenericDistrictLookup, Districts } from '../components/Outreach';
import LoadingSpinner from '@shared/ui/LoadingSpinner';

interface CampaignResponse {
  support_level: 'strongly_support' | 'support' | 'neutral' | 'oppose' | 'strongly_oppose';
  comments: string;
  contact_preferences: string[];
  demographics: {
    age_range?: string;
    gender?: string;
    location?: string;
    occupation?: string;
    political_affiliation?: string;
  };
  respondent_info: {
    name?: string;
    email?: string;
    phone?: string;
    zip_code?: string;
    address?: string;
    districts?: Districts;
  };
}

const CampaignResponsePage: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const source = searchParams.get('source') || 'direct';
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [response, setResponse] = useState<CampaignResponse>({
    support_level: 'neutral',
    comments: '',
    contact_preferences: [],
    demographics: {},
    respondent_info: {}
  });

  // Fetch campaign details
  const { data: campaign, isLoading, error } = useQuery(
    ['campaign-public', campaignId],
    async () => {
      const result = await api.get(`/campaigns/${campaignId}/public`);
      return result.data.data;
    },
    {
      enabled: !!campaignId,
      retry: false
    }
  );

  const handleInputChange = (field: string, value: any, category?: string) => {
    if (category) {
      setResponse(prev => ({
        ...prev,
        [category]: {
          ...(prev[category as keyof CampaignResponse] as object || {}),
          [field]: value
        }
      }));
    } else {
      setResponse(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleContactPreferenceChange = (preference: string, checked: boolean) => {
    setResponse(prev => ({
      ...prev,
      contact_preferences: checked 
        ? [...prev.contact_preferences, preference]
        : prev.contact_preferences.filter(p => p !== preference)
    }));
  };

  const handleSubmitResponse = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!response.support_level) {
      toast.error('Please indicate your level of support');
      return;
    }

    setIsSubmitting(true);

    try {
      await api.post(`/campaigns/${campaignId}/respond`, {
        ...response,
        source,
        submitted_at: new Date().toISOString()
      });

      toast.success('Thank you for your response! Your voice has been heard.');
      
      // Redirect to a thank you page or back to home
      setTimeout(() => {
        navigate('/');
      }, 2000);
      
    } catch (error: any) {
      console.error('Error submitting response:', error);
      toast.error(error.response?.data?.error || 'Failed to submit response');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAuthAction = (action: 'signin' | 'signup') => {
    // Store the response data in localStorage to preserve it
    localStorage.setItem('pendingCampaignResponse', JSON.stringify({
      campaignId,
      response,
      source
    }));
    
    // Navigate to auth page with return URL
    navigate(`/${action}?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background dark:bg-background-dark-primary flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="xl" className="mx-auto mb-4" />
          <p className="text-text-secondary dark:text-text-dark-secondary">Loading campaign...</p>
        </div>
      </div>
    );
  }

  if (error || !campaign) {
    const errorMessage = (error as any)?.response?.data?.error || '';
    const isNotActive = errorMessage.includes('not active');
    
    return (
      <div className="min-h-screen bg-background dark:bg-background-dark-primary flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary mb-2">
            {isNotActive ? 'Campaign Not Available' : 'Campaign Not Found'}
          </h1>
          <p className="text-text-secondary dark:text-text-dark-secondary mb-4">
            {isNotActive 
              ? 'This campaign is not currently active. It may be in draft mode or has already ended.'
              : 'The campaign you\'re looking for may have been removed or the link may be invalid.'
            }
          </p>
          {isNotActive && (
            <p className="text-text-tertiary dark:text-text-dark-tertiary text-sm">
              If you created this campaign, please launch it first to enable public access.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background dark:bg-background-dark-primary">
      {/* Header */}
      <div className="card rounded-none border-b border-border-light dark:border-border-dark">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center space-x-4">
            {campaign.organization?.logo_url ? (
              <img 
                src={campaign.organization.logo_url} 
                alt={`${campaign.organization.name} Logo`}
                className="w-12 h-12 rounded-lg object-cover"
              />
            ) : (
              <div className="w-12 h-12 bg-primary-600 text-white rounded-lg flex items-center justify-center">
                <Building className="w-6 h-6" />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary">{campaign.title}</h1>
              <p className="text-text-secondary dark:text-text-dark-secondary">
                From {campaign.organization?.name || 'Organization'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* About This Campaign - Left Column with Bold Outline */}
          <div className="lg:col-span-5">
            <div className="card border-4 border-primary-500 dark:border-primary-600 shadow-lg sticky top-6">
              <h2 className="text-xl font-bold text-text-primary dark:text-text-dark-primary mb-4 pb-3 border-b-2 border-primary-500 dark:border-primary-600">
                About This Campaign
              </h2>
              
              {campaign.description && (
                <div className="mb-4">
                  <p className="text-text-primary dark:text-text-dark-primary text-sm leading-relaxed">
                    {campaign.description}
                  </p>
                </div>
              )}

              <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4 mb-4">
                <p className="text-text-primary dark:text-text-dark-primary text-sm leading-relaxed whitespace-pre-wrap">
                  {campaign.message_to_users}
                </p>
              </div>

              {/* Related Information */}
              {campaign.linkedEntities && Object.keys(campaign.linkedEntities).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-text-secondary dark:text-text-dark-secondary mb-3">
                    Related Information
                  </h3>
                  <div className="space-y-2">
                    {campaign.linkedEntities.bill && campaign.linkedEntities.bill.map((bill: any, index: number) => (
                      <div 
                        key={index} 
                        className="flex items-center px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg text-sm font-medium hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                      >
                        📋 {bill.name}
                      </div>
                    ))}
                    {campaign.linkedEntities.legislator && campaign.linkedEntities.legislator.map((legislator: any, index: number) => (
                      <div 
                        key={index} 
                        className="flex items-center px-3 py-1.5 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg text-sm font-medium hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-colors"
                      >
                        👤 {legislator.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Response Form - Right Column */}
          <div className="lg:col-span-7">
            <div className="card">
              <div className="mb-6 pb-4 border-b border-border-light dark:border-border-dark">
                <h2 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary">Share Your Voice</h2>
              </div>

              <form onSubmit={handleSubmitResponse} className="space-y-6">
                {/* Support Level */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-3">
                    How do you feel about this campaign? *
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: 'strongly_support', label: 'Strongly Support', emoji: '💚', color: 'text-green-600' },
                      { value: 'support', label: 'Support', emoji: '👍', color: 'text-green-500' },
                      { value: 'neutral', label: 'Neutral', emoji: '😐', color: 'text-text-tertiary dark:text-text-dark-tertiary' },
                      { value: 'oppose', label: 'Oppose', emoji: '👎', color: 'text-red-500' },
                      { value: 'strongly_oppose', label: 'Strongly Oppose', emoji: '❌', color: 'text-red-600' }
                    ].map((option) => (
                      <label key={option.value} className="flex items-center p-3 border border-border-light dark:border-border-dark rounded-lg hover:bg-background-tertiary dark:hover:bg-background-dark-secondary cursor-pointer transition-colors">
                        <input
                          type="radio"
                          name="support_level"
                          value={option.value}
                          checked={response.support_level === option.value}
                          onChange={(e) => handleInputChange('support_level', e.target.value)}
                          className="mr-3"
                        />
                        <span className="text-lg mr-2">{option.emoji}</span>
                        <span className={`font-medium ${option.color}`}>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Comments */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                      Additional Comments (Optional)
                    </label>
                    <span className={`text-xs ${
                      response.comments.length > 2000 
                        ? 'text-red-500 font-medium' 
                        : response.comments.length > 1800 
                          ? 'text-amber-500' 
                          : 'text-text-tertiary dark:text-text-dark-tertiary'
                    }`}>
                      {response.comments.length}/2000
                    </span>
                  </div>
                  <textarea
                    rows={4}
                    value={response.comments}
                    onChange={(e) => handleInputChange('comments', e.target.value)}
                    placeholder="Share your thoughts, concerns, or suggestions..."
                    className={`input ${response.comments.length > 2000 ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                  />
                  {response.comments.length > 2000 && (
                    <p className="text-red-500 text-xs mt-1">
                      Comments must be less than 2000 characters ({response.comments.length - 2000} over)
                    </p>
                  )}
                </div>

                {/* Contact Information */}
                <div className="border-t border-border-light dark:border-border-dark pt-6">
                  <h3 className="text-lg font-medium text-text-primary dark:text-text-dark-primary mb-4">Contact Information (Optional)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                        <Mail className="w-4 h-4 inline mr-1" />
                        Email
                      </label>
                      <input
                        type="email"
                        value={response.respondent_info.email || ''}
                        onChange={(e) => handleInputChange('email', e.target.value, 'respondent_info')}
                        className="input"
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                        <Users className="w-4 h-4 inline mr-1" />
                        Name
                      </label>
                      <input
                        type="text"
                        value={response.respondent_info.name || ''}
                        onChange={(e) => handleInputChange('name', e.target.value, 'respondent_info')}
                        className="input"
                        placeholder="Your Name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                        <Phone className="w-4 h-4 inline mr-1" />
                        Phone (Optional)
                      </label>
                      <input
                        type="tel"
                        value={response.respondent_info.phone || ''}
                        onChange={(e) => handleInputChange('phone', e.target.value, 'respondent_info')}
                        className="input"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                        <MapPin className="w-4 h-4 inline mr-1" />
                        ZIP Code
                      </label>
                      <input
                        type="text"
                        value={response.respondent_info.zip_code || ''}
                        onChange={(e) => handleInputChange('zip_code', e.target.value, 'respondent_info')}
                        className="input"
                        placeholder="12345"
                      />
                    </div>
                  </div>

                  {/* District Lookup */}
                  <div className="mt-4">
                    <GenericDistrictLookup
                      itemId={campaignId || ''}
                      onDistrictsFound={(districts, formattedAddress) => {
                        setResponse(prev => ({
                          ...prev,
                          respondent_info: {
                            ...prev.respondent_info,
                            address: formattedAddress,
                            districts
                          }
                        }));
                      }}
                    />
                  </div>
                </div>

                {/* Demographics */}
                <div className="border-t border-border-light dark:border-border-dark pt-6">
                  <h3 className="text-lg font-medium text-text-primary dark:text-text-dark-primary mb-4">Demographics (Optional - Helps with Analysis)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                        <Calendar className="w-4 h-4 inline mr-1" />
                        Age Range
                      </label>
                      <select
                        value={response.demographics.age_range || ''}
                        onChange={(e) => handleInputChange('age_range', e.target.value, 'demographics')}
                        className="input"
                      >
                        <option value="">Select Age Range</option>
                        <option value="18-24">18-24</option>
                        <option value="25-34">25-34</option>
                        <option value="35-44">35-44</option>
                        <option value="45-54">45-54</option>
                        <option value="55-64">55-64</option>
                        <option value="65+">65+</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                        <Briefcase className="w-4 h-4 inline mr-1" />
                        Occupation
                      </label>
                      <input
                        type="text"
                        value={response.demographics.occupation || ''}
                        onChange={(e) => handleInputChange('occupation', e.target.value, 'demographics')}
                        className="input"
                        placeholder="Your profession"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Preferences */}
                <div className="border-t border-border-light dark:border-border-dark pt-6">
                  <h3 className="text-lg font-medium text-text-primary dark:text-text-dark-primary mb-4">How would you like to stay engaged?</h3>
                  <div className="space-y-2">
                    {[
                      { value: 'email_updates', label: 'Email updates on similar campaigns' },
                      { value: 'text_alerts', label: 'Text alerts for urgent actions' },
                      { value: 'volunteer', label: 'Volunteer opportunities' },
                      { value: 'events', label: 'Event invitations' }
                    ].map((option) => (
                      <label key={option.value} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={response.contact_preferences.includes(option.value)}
                          onChange={(e) => handleContactPreferenceChange(option.value, e.target.checked)}
                          className="mr-3"
                        />
                        <span className="text-sm text-text-primary dark:text-text-dark-primary">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Submit Actions */}
                <div className="border-t border-border-light dark:border-border-dark pt-6">
                  <div className="flex flex-col space-y-4">
                    {/* Primary Submit */}
                    <button
                      type="submit"
                      disabled={isSubmitting || response.comments.length > 2000}
                      className="w-full bg-primary-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isSubmitting ? 'Submitting Response...' : 'Submit Response'}
                    </button>

                    {/* Auth Options */}
                    <div className="text-center">
                      <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-3">
                        Want to create an account to track your advocacy impact?
                      </p>
                      <div className="flex space-x-4 justify-center">
                        <button
                          type="button"
                          onClick={() => handleAuthAction('signin')}
                          className="px-4 py-2 text-primary-600 border border-primary-600 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                        >
                          Sign In
                        </button>
                        <button
                          type="button"
                          onClick={() => handleAuthAction('signup')}
                          className="px-4 py-2 bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 dark:bg-primary-900/30 dark:text-primary-300 dark:hover:bg-primary-900/40 transition-colors"
                        >
                          Create Account
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignResponsePage;
